app.component('richtextEditor', {
    bindings: {
        data: '=', // data is string type, so we use 2-ways binding
        maxLength: '<?',
        toolbar: '@?',
        id: '@?',
        focus: '@?',
        title: '@?',
        placeholder: '@?',
        testId: '@?'
    },
    controller: ['$scope', '$timeout', '$log', '$element', '$sanitize', function ($scope, $timeout, $log, $element, $sanitize) {
        const $ctrl = this;
        let quillInstance = null;
        let isInternalChange = false;
        let textChangeHandler = null;

        $ctrl.$onInit = () => {
            const baseId = (typeof $ctrl.id === 'string' && $ctrl.id) ? $ctrl.id : ('richtextEditor' + $scope.$id);
            $ctrl.editorId = baseId;

            $timeout(() => {
                const hostElement = $element[0].querySelector(`#${$ctrl.editorId}`);
                if (!hostElement) {
                    $log.error('Quill host element not found:', $ctrl.editorId);
                    return;
                }

                const resolvedTestId = ($ctrl.testId && String($ctrl.testId).trim())
                    ? String($ctrl.testId).trim()
                    : String($ctrl.editorId).replace(/_/g, '-');

                // Register the selectable font whitelist once. Values must be
                // CSS-class-safe (no spaces); friendly names/typefaces are mapped
                // to these values via `.ql-font-*` rules in _richtext.css.
                const Font = Quill.import('formats/font');
                Font.whitelist = ['arial', 'georgia', 'times-new-roman', 'courier-new', 'verdana'];
                Quill.register(Font, true);

                const defaultToolbar = [
                    // Pass the options explicitly. An empty array builds a dropdown
                    // with only the default option, so the selection can't be
                    // changed. `false` is the default (Gordita Regular) option,
                    // which also lets users clear back to it after picking a font.
                    [{ 'font': [false].concat(Font.whitelist) }],
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'link'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    ['clean']
                ];
                const toolbarOptions = $ctrl.toolbar ? JSON.parse($ctrl.toolbar) : defaultToolbar;
                const matchers = [
                    [Node.ELEMENT_NODE, (node, delta) => {
                        const attributes = {};
                        const align = node.style.textAlign;
                        if (align) { attributes.align = align; }
                        const color = node.style.color;
                        if (color) { attributes.color = color; }
                        const background = node.style.backgroundColor;
                        if (background) { attributes.background = background; }
                        if (Object.keys(attributes).length > 0) {
                            delta.ops.forEach(op => {
                                op.attributes = { ...(op.attributes || {}), ...attributes };
                            });
                        }
                        return delta;
                    }]
                ];
                const options = {
                    theme: 'snow',
                    modules: {
                        toolbar: toolbarOptions,
                        clipboard: { matchers: matchers }
                    },
                    placeholder: $ctrl.placeholder || 'Type your content here...'
                };

                quillInstance = new Quill(hostElement, options);

                hostElement.setAttribute('data-testid', resolvedTestId);

                if ($ctrl.data) {
                    isInternalChange = true;
                    quillInstance.clipboard.dangerouslyPasteHTML(0, $sanitize($ctrl.data));
                    $timeout(() => {
                        isInternalChange = false;
                    }, 0);
                }

                textChangeHandler = (delta, oldDelta, source) => {
                    if (source === 'user') {
                        if ($ctrl.maxLength) {
                            const textLength = quillInstance.getLength() - 1;
                            if (textLength > $ctrl.maxLength) {
                                quillInstance.deleteText($ctrl.maxLength, textLength - $ctrl.maxLength);
                            }
                        }

                        let content = quillInstance.root.innerHTML;
                        if (content === '<p><br></p>') {
                            content = '';
                        }

                        $timeout(() => {
                            isInternalChange = true;
                            $ctrl.data = content;
                            $timeout(() => { isInternalChange = false; }, 0);
                        });
                    }
                };

                quillInstance.on('text-change', textChangeHandler);

                if ($ctrl.focus) {
                    if ($element[0].offsetParent !== null) {
                        quillInstance.focus();
                    } else {
                        $log.warn('richtextEditor: "focus" attribute was set, but editor is not visible. Skipping focus.');
                    }
                }

            }, 0);
        };

        $scope.$watch(
            "$ctrl.data",
            function (newValue) {
                if (isInternalChange) {
                    return;
                }

                if (quillInstance) {
                    const modelValue = newValue || "";
                    const editorHtml = quillInstance.root.innerHTML;
                    const editorValue = (editorHtml === '<p><br></p>') ? '' : editorHtml;

                    if (modelValue !== editorValue) {
                        const delta = quillInstance.clipboard.convert($sanitize(modelValue));
                        quillInstance.setContents(delta, 'api');
                    }
                }
            }
        );

        $ctrl.$onDestroy = () => {
            if (quillInstance) {
                if (textChangeHandler) {
                    quillInstance.off('text-change', textChangeHandler);
                }
                textChangeHandler = null;
                quillInstance = null;
            }
        };

        $scope.$on('editor:insert-html', function (event, payload) {
            if (quillInstance && payload.editorId === $ctrl.editorId) {

                // 1. Force focus back to the editor
                quillInstance.focus();

                // 2. Get the user's last cursor position (or default to the very end)
                const range = quillInstance.getSelection();
                const index = range ? range.index : quillInstance.getLength();

                // 3. Paste the HTML into Quill
                quillInstance.clipboard.dangerouslyPasteHTML(index, $sanitize(payload.html || ''));

                // 4. Manually trigger a data update to ensure Angular's 2-way binding catches it
                $timeout(() => {
                    isInternalChange = true;
                    $ctrl.data = quillInstance.root.innerHTML;
                    $timeout(() => { isInternalChange = false; }, 0);
                });
            }
        });

        // Appends as a new block at the very end of the document, ignoring the caret. Used for generated
        // record lines (standing orders) that must not be spliced into the middle of a sentence.
        // Contrast 'editor:insert-html' above, which is caret-relative and used for transcription inserts.
        $scope.$on('editor:append-html', function (event, payload) {
            if (quillInstance && payload.editorId === $ctrl.editorId) {

                // getLength() counts the trailing newline, so -1 lands the paste just before it and the
                // content becomes a new trailing block rather than being pushed past the end.
                const index = Math.max(quillInstance.getLength() - 1, 0);
                quillInstance.clipboard.dangerouslyPasteHTML(index, $sanitize(payload.html || ''));
                quillInstance.setSelection(quillInstance.getLength(), 0);

                $timeout(() => {
                    isInternalChange = true;
                    $ctrl.data = quillInstance.root.innerHTML;
                    $timeout(() => { isInternalChange = false; }, 0);
                });
            }
        });
    }],
    templateUrl: 'app/src/js/components/richtextEditor/richtextEditor.html'
});